import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";

/**
 * Form card chrome — the `.ec-box` Paper shell shared by the onboarding forms.
 * Border/surface use colors_v2 tokens directly (--new-border / --app-body); the
 * drop shadow is an inline literal (colors_v2 has no shadow token).
 */
const FORM_CARD = {
  radius: 8,
  border: "1px solid var(--new-border)",
  shadow:
    "0 2px 4px -2px rgba(13, 0, 0, 0.1), 0 4px 6px -1px rgba(13, 0, 0, 0.1)",
  bg: "var(--app-body)",
  padding: "16px",
  bodyGap: "16px",
  headerFontSize: 16,
  headerFontWeight: 600,
  headerLineHeight: 1.5,
  footerBorderCard: "1px solid var(--new-border)",
  footerBorderRegister: "1px solid var(--new-border)"
};

/**
 * BaseFormCard — the canonical reskin form card shell shared by the onboarding
 * screens (Register, Login, ForgotPassword, CompanyInfo, Verify, UserInfo).
 *
 * WHAT IT IS: the chrome around an onboarding form — a bordered/shadowed
 * `.ec-box` Paper with a header BAR (title, bottom border) and an optional
 * footer BAR (bordered, holding the action buttons). The form body lives in
 * between. It owns ONLY the frame; fields and buttons are passed in as
 * `children` / `footer`.
 *
 * The canonical body layout (used by every screen) is: a padded `16px` flex
 * column whose first child is the form description, followed by the fields.
 * That body markup is supplied by the caller as `children` so each screen keeps
 * its exact internal structure.
 *
 * WHAT IT WRAPS: MUI `Paper` / `Box` / `Typography` plus a native `<form>`.
 *
 * CONTRACT (props in):
 *  - title: i18n message id for the header (omit → no header).
 *  - headerExtra: node rendered beside the title.
 *  - onSubmit: form submit handler.
 *  - children: the form interior (the padded body + any validation rows).
 *  - footer: node for the bordered footer bar (omit → no footer).
 *  - footerJustify: "space-between" | "flex-end" (default "space-between") —
 *      footer flex justification.
 *  - footerVariant: "card" | "register" (default "card") — "register" uses the
 *      double literal-grey border; "card" uses the single top --Card-border.
 *  - footerMinHeight / footerHeight: footer sizing overrides (default 62px high;
 *      pass footerHeight={null} to drop the fixed height, e.g. ForgotPassword).
 *  - belowForm: node rendered inside the card BELOW the form (the "* required"
 *      note, product legal text, a loading view, etc.).
 *  - footerSx / paperSx / sx: style overrides merged over the footer / Paper.
 *  - paperStyle: inline-style overrides merged over the base card chrome
 *      (border / radius / shadow / surface). Use this — not paperSx — to swap a
 *      chrome property, since the chrome is applied via the inline `style` prop
 *      (which wins over sx). e.g. the subscription cards' container geometry.
 *  - noForm (default false): when true, render children + footer directly
 *      instead of wrapping them in a <form>; onSubmit is ignored. For
 *      presentational cards (e.g. the subscription cards) that are not forms.
 *  - headerBorder (default true): when false, the header bar drops its bottom
 *      border. Header geometry is otherwise unchanged.
 *  - headerSx: style overrides merged over the header bar Box (e.g. the card
 *      header height/padding/gap).
 *  - titleColor: overrides the header title color (default
 *      var(--font-strong-default)); applied with !important like the default.
 *  - titleProps: extra props for the title Typography — `data-testid` is
 *      forwarded and `sx` is merged over the default title styles (lets a
 *      caller restyle the title, e.g. the 16/600 card headline).
 *  - data-testid: forwarded to the Paper.
 *  - ...props: spread onto the Paper (e.g. onClick / role / tabIndex).
 *
 * BEHAVIOR (out): the same card frame each onboarding screen renders today —
 * same borders, padding, header/footer geometry — with the form-specific
 * content supplied by the caller.
 *
 * MIGRATION NOTE (shadcn): this is Card + CardHeader + CardContent +
 * CardFooter. `title` = `<CardTitle>`, `children` = `<CardContent>`, `footer` =
 * `<CardFooter>`. The `--Card-*` tokens already drive the colors via FORM_CARD.
 */
/**
 * Base card chrome (border / radius / shadow / surface) — applied via the inline
 * `style` prop exactly as ec-main-ui's BaseFormCard does, so every form card
 * renders an identical border/surface. Callers that need to override a single
 * property (e.g. the subscription panel swapping the surface, or the
 * subscription cards replacing the geometry) pass `paperStyle`, which is merged
 * over this base.
 */
const ecBoxStyle = {
  borderRadius: FORM_CARD.radius,
  width: "100%",
  border: FORM_CARD.border,
  boxShadow: FORM_CARD.shadow,
  backgroundColor: FORM_CARD.bg
};

const BaseFormCard = ({
  title,
  headerExtra,
  onSubmit,
  children,
  footer,
  footerJustify = "space-between",
  footerVariant = "card",
  footerMinHeight,
  footerHeight = "62px",
  belowForm,
  footerSx,
  paperSx,
  paperStyle,
  sx,
  noForm = false,
  headerBorder = true,
  headerSx,
  titleColor,
  titleProps,
  ...props
}) => {
  const footerBorder =
    footerVariant === "register"
      ? FORM_CARD.footerBorderRegister
      : FORM_CARD.footerBorderCard;

  // Footer bar shared by both render modes; in noForm mode it lives directly in
  // the Paper instead of inside a <form>.
  const footerBar = footer && (
    <Box
      sx={{
        ...(footerHeight ? { height: footerHeight } : {}),
        ...(footerMinHeight ? { minHeight: footerMinHeight } : {}),
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: footerJustify,
        gap: "12px",
        padding: FORM_CARD.padding,
        borderTop: footerBorder,
        ...(footerVariant === "register"
          ? { borderBottom: footerBorder }
          : {}),
        ...footerSx
      }}
    >
      {footer}
    </Box>
  );

  return (
    <Paper
      className="ec-box"
      elevation={0}
      // Chrome via inline style (matches ec-main-ui exactly); paperStyle merges
      // over it for callers that swap a single chrome property.
      style={{ ...ecBoxStyle, ...paperStyle }}
      sx={{ ...paperSx, ...sx }}
      {...props}
    >
      {(title || headerExtra) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "8px",
            p: "10px 16px",
            ...(headerBorder ? { borderBottom: FORM_CARD.border } : {}),
            ...headerSx
          }}
        >
          {title && (
            <Typography
              component="p"
              data-testid={titleProps?.["data-testid"]}
              sx={{
                m: 0,
                fontSize: FORM_CARD.headerFontSize,
                fontWeight: FORM_CARD.headerFontWeight,
                lineHeight: FORM_CARD.headerLineHeight,
                color: `${
                  titleColor || "var(--font-strong-default)"
                } !important`,
                ...titleProps?.sx
              }}
            >
              <FormattedMessage id={title} />
            </Typography>
          )}
          {headerExtra}
        </Box>
      )}

      {/* noForm mode (presentational cards): render the body + footer directly,
          no <form> wrapper and onSubmit is ignored. The wrapping <div> mirrors
          the <form>'s `text-align: left` so the body is styled identically. */}
      {noForm ? (
        <div style={{ textAlign: "left" }}>
          {children}
          {footerBar}
        </div>
      ) : (
        /* noValidate disables the browser's native constraint-validation popups
           (the "Please fill in this field" bubble) so only our styled per-field
           messages show; validation is owned by react-hook-form / the screens. */
        <form style={{ textAlign: "left" }} onSubmit={onSubmit} noValidate>
          {/* The form interior (padded body + any validation rows) is supplied
              verbatim as `children` so each screen keeps its exact internal
              structure; only the footer bar below is a structured slot. */}
          {children}
          {footerBar}
        </form>
      )}

      {belowForm}
    </Paper>
  );
};

export default BaseFormCard;
